import { h, FunctionComponent } from 'preact';
import FullSite from './full-site';
import { msg, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState, useEffect, useCallback } from 'preact/hooks';

import { ListApiResponse, SlotTournamentsService, TournamentSummary } from '../../../services/slot-tournaments';
import { TournamentCarousel } from '../desktop-components/TournamentCarousel';
import { MacroMessageDescriptor } from '@lingui/core/macro';

// This component contains the core logic and rendering for the tournament list,
// designed to be used within different layout wrappers (desktop/mobile).
export const TournamentListContent: FunctionComponent = () => {
    const [service] = useState<SlotTournamentsService>(() => new SlotTournamentsService());
    // State for different tournament categories
    const [activeTournaments, setActiveTournaments] = useState<TournamentSummary[]>([]);
    const [upcomingTournaments, setUpcomingTournaments] = useState<TournamentSummary[]>([]);
    const [finishedTournaments, setFinishedTournaments] = useState<TournamentSummary[]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<MacroMessageDescriptor | null>(null);

    // Fetch all tournaments with a single call and categorize them
    const fetchAllTournaments = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await service.listTournaments('all', 1, 100); // Fetch more items

            const allTournaments = response.data || [];

            const active = allTournaments.filter(t => t.status === 'active');
            const upcoming = allTournaments.filter(t => t.status === 'upcoming');
            const finished = allTournaments.filter(t => t.status === 'finished');

            setActiveTournaments(active);
            setUpcomingTournaments(upcoming);
            setFinishedTournaments(finished);

        } catch (err: any) {
            console.error("Failed to fetch tournaments:", err);
            setError(msg`Failed to load tournaments.`);
            // Clear all lists on failure
            setActiveTournaments([]);
            setUpcomingTournaments([]);
            setFinishedTournaments([]);
        } finally {
            setLoading(false);
        }
    }, [service]);

    useEffect(() => {
        fetchAllTournaments();
    }, [fetchAllTournaments]);

    const renderLoading = () => (
        <div class="text-center p-10 text-white"><Trans>Loading tournaments...</Trans></div>
    );

    const renderError = () => (
        <div class="text-center p-10 text-red-600">
            <Trans>Error</Trans>: window.Translation(error)
        </div>
    );

    // The TournamentCarousel handles the "no tournaments" case internally.

    return (
        <div class="p-4 md:p-8 bg-harif-800 text-slate-50">
            <h1 class="text-3xl mb-8 text-center"><Trans>Slot Tournaments</Trans></h1>

            {loading && renderLoading()}
            {error && !loading && renderError()}

            {!loading && !error && (
                <div class="max-w-6xl mx-auto">
                    {/* Render carousels for each category */}
                    <TournamentCarousel
                        title={t`Active Tournaments`}
                        tournaments={activeTournaments}
                    />
                    <TournamentCarousel
                        title={t`Announced Tournaments`}
                        tournaments={upcomingTournaments}
                    />
                    <TournamentCarousel
                        title={t`Concluded Tournaments`}
                        tournaments={finishedTournaments}
                    />
                </div>
            )}
        </div>
    );
};
// This component now acts as a simple wrapper for the desktop view
export const TournamentListPage: FunctionComponent = () => { // Use FunctionComponent
    // All state, data fetching, and rendering logic has been moved to TournamentListContent
    return (
        <FullSite>
            {/* Render the shared content component */}
            <TournamentListContent />
        </FullSite>
    );
};